import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Tabs, Tab, Paper,
  Grid, TextField, Alert, CircularProgress, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Menu, ListItemIcon, ListItemText, Card, CardContent, Breadcrumbs, Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  Shield as ShieldIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  AssignmentInd as RoleIcon,
  StoreMallDirectory as StoreAssignIcon,
  RemoveCircle as RemoveIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  ToggleOff as DeactivateIcon,
  ToggleOn as ActivateIcon,
  NavigateNext as NavigateNextIcon,
  Group as GroupIcon,
  Badge as BadgeIcon,
  LocationOn as LocationIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  LocalOffer as OfferIcon,
  LocalShipping as DeliveryIcon,
  CreditCard as CreditNoteIcon,
  ShoppingCart as ShoppingCartIcon,
  Build as RepairIcon,
  Drafts as DraftsIcon,
  CheckCircleOutline as ClosedIcon,
  PersonOutline as CustomerIcon,
  Inventory2 as ProductIcon,
  LocalAtm as SupplierIcon,
  ShoppingBag as PurchaseIcon,
  CloudDownload as PeppolIncomingIcon,
  Storefront as OnlineStoreIcon,
  ConfirmationNumber as TicketIcon,
  LocalMall as SaleOrderIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getCompanyById,
  getCompanyDetailStats,
  updateCompanyDetails,
  getCompanyStores,
  createStore,
  updateStore,
  deleteStore,
  changeUserRole,
  getAvailableRoles,
  getUserPermissions,
  updateUserPermissions,
  getAllModules,
  enableUser,
  disableUser,
  deleteUser,
  assignUserToStore,
  removeUserFromStore,
  adminChangePassword,
  adminChangeUsername,
  adminChangeEmail,
  activateCompany,
  deactivateCompany,
} from '../services/adminUserService';
import StoreDialog from '../components/StoreDialog';
import PermissionsMatrix from '../components/PermissionsMatrix';

// ─── Tab Panel ────────────────────────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && children}
    </div>
  );
}

// ─── Company Info Fields ──────────────────────────────────────────────────
const COMPANY_FIELDS = [
  { name: 'name',                  label: 'Company Name',          required: true, grid: 12 },
  { name: 'email',                 label: 'Email',                 grid: 6 },
  { name: 'phoneNumber',           label: 'Phone Number',          grid: 6 },
  { name: 'btwNumber',             label: 'BTW / VAT Number',      grid: 6 },
  { name: 'ibaNumber',             label: 'IBAN Number',           grid: 6 },
  { name: 'bicNumber',             label: 'BIC Number',            grid: 6 },
  { name: 'rprNumber',             label: 'RPR Number',            grid: 6 },
  { name: 'website',               label: 'Website',               grid: 6 },
  { name: 'companyPeppolId',       label: 'Company Peppol ID',     grid: 6 },
  { name: 'peppolUserId',          label: 'Peppol User ID',        grid: 6 },
  { name: 'peppolUserMotdePasse',  label: 'Peppol Password',       grid: 6, type: 'password' },
  { name: 'sendInvoiceLimit',      label: 'Send Invoice Limit',    grid: 6, type: 'number' },
  { name: 'subscriptionFee',       label: 'Subscription Fee',      grid: 6, type: 'number' },
  { name: 'street',                label: 'Street',                grid: 6 },
  { name: 'streetNumber',          label: 'Street No.',            grid: 3 },
  { name: 'postCode',              label: 'Post Code',             grid: 3 },
  { name: 'city',                  label: 'City',                  grid: 6 },
  { name: 'country',               label: 'Country',               grid: 6 },
];

// ─── Statistics Card Component ────────────────────────────────────────────
function StatisticsCard({ icon: Icon, title, value, color, bgColor }) {
  return (
    <Card sx={{ 
      height: '100%',
      border: '1px solid #e0e0e0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      }
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon sx={{ fontSize: 24, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Delete Store Confirmation Dialog ─────────────────────────────────────
function ConfirmDeleteStoreDialog({ open, store, onConfirm, onClose, loading }) {
  if (!store) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '0.95rem', color: '#f44336', pb: 1 }}>
        <WarningIcon fontSize="small" /> Delete Store
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Permanently delete store <strong>{store.storeName || store.StoreName}</strong>?<br />
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
            bgcolor: '#f44336', '&:hover': { bgcolor: '#c62828' },
            '&.Mui-disabled': { bgcolor: '#e0e0e0' },
          }}
        >
          {loading ? 'Deleting…' : 'Yes, Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Module Order Configuration (matches sidebar sequence) ─────────────────
/**
 * MODULE_ORDER: Defines the sequence of modules in the permissions matrix
 * This order matches the sidebar navigation structure for consistency.
 * 
 * Key Sales Modules (in sequence):
 * - Offers: Sales proposals/quotations
 * - Sale Orders: Confirmed sales orders
 * - Delivery Notes: Shipping/delivery documents
 * - Invoices: Sales invoices (including Peppol)
 * - CreditNotes: Credit notes/refunds
 * 
 * Permissions (View, Edit, Create, Delete) are managed per module.
 * - CanView must be enabled before other permissions
 * - Module visibility in sidebar is controlled by user's View permission
 * - Action buttons (Edit/Create/Delete) are shown based on respective permissions
 */
const MODULE_ORDER = [
  'POS',
  'Dashboard',
  'Customers',
  'Suppliers',
  'Products',
  'Wishlist',
  'Offers',
  'Sale Orders',
  'Delivery Notes',
  'Invoices',
  'CreditNotes',
  'ReceivedInvoices',
  'Purchases',
  'Payments',
  'Reports',
  'Inventory',
  'Company Settings',
  'User Settings',
  'Announcements',
];

// ─── User Card Component ──────────────────────────────────────────────────
function UserCard({ user, showStoreName = false, onMenuOpen }) {
  const getUserId = (user) => user.UserId || user.userId || user.Id || user.id;
  const fullName = user.FullName || user.fullName || user.UserName || user.userName || user.Name || user.name || 'Unknown';
  const email = user.Email || user.email || '';
  const isActive = user.IsActive ?? user.isActive ?? true;
  const storeName = user.StoreName || user.storeName;
  
  return (
    <Card 
      sx={{ 
        mb: 1.5, 
        border: '1px solid',
        borderColor: isActive ? 'divider' : 'error.light',
        backgroundColor: isActive ? 'background.paper' : 'action.disabledBackground',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
            <Avatar 
              sx={{ 
                bgcolor: isActive ? 'primary.main' : 'grey.500',
                width: 40,
                height: 40 
              }}
            >
              {fullName.charAt(0).toUpperCase()}
            </Avatar>
            
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {fullName}
                </Typography>
                {!isActive && (
                  <Chip 
                    label="Inactive" 
                    size="small" 
                    color="error" 
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {email}
              </Typography>
              {showStoreName && storeName && (
                <Chip 
                  label={storeName} 
                  size="small" 
                  icon={<StoreIcon />}
                  sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
          
          <IconButton 
            size="small" 
            onClick={(e) => onMenuOpen && onMenuOpen(e, user)}
            sx={{ ml: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────
const CompanyEditPage = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Company data
  const [company, setCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({});
  const [originalCompanyForm, setOriginalCompanyForm] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Company Statistics
  const [companyStats, setCompanyStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Stores
  const [stores, setStores] = useState([]);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [deleteStoreTarget, setDeleteStoreTarget] = useState(null);
  const [deletingStore, setDeletingStore] = useState(false);
  
  // Users & Roles
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [allModules, setAllModules] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Sort and validate modules to match sidebar sequence
  const sortModulesBySidebar = useCallback((modules) => {
    if (!Array.isArray(modules) || modules.length === 0) return [];
    
    // Create a map for quick lookup
    const moduleMap = new Map();
    modules.forEach(mod => {
      const name = mod.ModuleName || mod.Name || mod.name;
      if (name) moduleMap.set(name, mod);
    });
    
    // Sort according to MODULE_ORDER
    const sorted = [];
    MODULE_ORDER.forEach(moduleName => {
      if (moduleMap.has(moduleName)) {
        sorted.push(moduleMap.get(moduleName));
        moduleMap.delete(moduleName);
      }
    });
    
    // Add any remaining modules not in the order list
    moduleMap.forEach(mod => sorted.push(mod));
    
    return sorted;
  }, []);

  // User Management tab state
  const [userActionMenuAnchor, setUserActionMenuAnchor] = useState(null);
  const [userActionMenuTarget, setUserActionMenuTarget] = useState(null);
  const [userMgmtDialog, setUserMgmtDialog] = useState({ type: null, user: null, open: false });
  const [userMgmtLoading, setUserMgmtLoading] = useState(false);
  const [dlgField1, setDlgField1] = useState('');
  const [dlgField2, setDlgField2] = useState('');
  const [dlgField3, setDlgField3] = useState('');

  // ─── Group Users By Store ─────────────────────────────────────────────────
  const groupedUsers = useMemo(() => {
    const grouped = {
      unassigned: users.filter(user => !user.StoreId && !user.storeId),
    };
    
    stores.forEach(store => {
      const storeId = store.Id || store.id;
      grouped[storeId] = users.filter(user => 
        (user.StoreId || user.storeId) === storeId
      );
    });
    
    return grouped;
  }, [users, stores]);

  // ─── Load Company Data ────────────────────────────────────────────────────
  const loadCompanyData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load comprehensive company details from new endpoint
      const companyData = await getCompanyById(companyId);
      
      if (!companyData || !companyData.Company) {
        setError('Company not found');
        setLoading(false);
        return;
      }
      
      // Map the response structure (API returns PascalCase)
      const companyInfo = companyData.Company;
      
      // Create a company object compatible with existing code
      const company = {
        id: companyInfo.Id,
        name: companyInfo.CompanyName,
        email: companyInfo.Email,
        phoneNumber: companyInfo.PhoneNumber,
        btwNumber: companyInfo.BTWNumber,
        street: companyInfo.Street,
        streetNumber: companyInfo.StreetNumber,
        postCode: companyInfo.PostCode,
        city: companyInfo.City,
        country: companyInfo.Country,
        website: companyInfo.Website,
        companyPeppolId: companyInfo.CompanyPeppolId,
        isActive: companyInfo.IsActive,
        createdDate: companyInfo.CreatedDate,
        users: companyData.Users || [],
        stores: companyData.Stores || [],
        statistics: companyData.Statistics,
      };
      
      setCompany(company);
      
      // Initialize company form
      const form = {
        name: companyInfo.CompanyName || '',
        email: companyInfo.Email || '',
        phoneNumber: companyInfo.PhoneNumber || '',
        btwNumber: companyInfo.BTWNumber || '',
        ibaNumber: companyInfo.IBANumber || '',
        bicNumber: companyInfo.BICNumber || '',
        rprNumber: companyInfo.RPRNumber || '',
        website: companyInfo.Website || '',
        companyPeppolId: companyInfo.CompanyPeppolId || '',
        peppolUserId: companyInfo.PeppolUserId || '',
        peppolUserMotdePasse: companyInfo.PeppolUserMotdePasse || '',
        sendInvoiceLimit: companyInfo.SendInvoiceLimit ?? '',
        subscriptionFee: companyInfo.SubscriptionFee ?? '',
        street: companyInfo.Street || '',
        streetNumber: companyInfo.StreetNumber || '',
        postCode: companyInfo.PostCode || '',
        city: companyInfo.City || '',
        country: companyInfo.Country || '',
      };
      setCompanyForm(form);
      setOriginalCompanyForm(form);
      setHasChanges(false);
      
      // Set stores directly from response
      setStores(companyData.Stores || []);
      
      // Set users from response
      setUsers(companyData.Users || []);
      
      // Load available roles
      const rolesResponse = await getAvailableRoles();
      const rolesList = rolesResponse?.roles || rolesResponse?.payload || rolesResponse?.data || rolesResponse || [];
      setAvailableRoles(Array.isArray(rolesList) ? rolesList : []);
      
      // Load all modules for permissions
      const modulesResponse = await getAllModules();
      const modulesList = modulesResponse?.payload || modulesResponse?.data || modulesResponse || [];
      const modulesArray = Array.isArray(modulesList) ? modulesList : [];
      
      // Sort modules to match sidebar sequence
      const sortedModules = sortModulesBySidebar(modulesArray);
      setAllModules(sortedModules);
      
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  }, [companyId, sortModulesBySidebar]);

  // ─── Load Company Statistics ──────────────────────────────────────────────
  const loadCompanyStats = useCallback(async () => {
    if (!companyId) return;
    setStatsLoading(true);
    try {
      const stats = await getCompanyDetailStats(companyId);
      setCompanyStats(stats);
    } catch (e) {
      console.error('Failed to load company statistics:', e);
      // Don't show error toast - stats are optional
    } finally {
      setStatsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadCompanyData();
      loadCompanyStats();
    }
  }, [companyId, loadCompanyData, loadCompanyStats]);

  // ─── Save Company Info ────────────────────────────────────────────────────
  const handleSaveCompanyInfo = async () => {
    if (!companyForm.name?.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      // Map to UpdateCompanyDetailsDto structure
      const updateDto = {
        id: parseInt(companyId),
        companyName: companyForm.name,
        email: companyForm.email || null,
        phoneNumber: companyForm.phoneNumber || null,
        btwNumber: companyForm.btwNumber || null,
        ibaNumber: companyForm.ibaNumber || null,
        bicNumber: companyForm.bicNumber || null,
        rprNumber: companyForm.rprNumber || null,
        website: companyForm.website || null,
        companyPeppolId: companyForm.companyPeppolId || null,
        peppolUserId: companyForm.peppolUserId || null,
        peppolUserMotdePasse: companyForm.peppolUserMotdePasse || null,
        sendInvoiceLimit: companyForm.sendInvoiceLimit !== '' ? Number(companyForm.sendInvoiceLimit) : null,
        subscriptionFee: companyForm.subscriptionFee !== '' ? Number(companyForm.subscriptionFee) : null,
        street: companyForm.street || null,
        streetNumber: companyForm.streetNumber || null,
        postCode: companyForm.postCode || null,
        city: companyForm.city || null,
        country: companyForm.country || null,
      };
      
      await updateCompanyDetails(updateDto);
      toast.success('Company information updated successfully');
      setOriginalCompanyForm(companyForm);
      setHasChanges(false);
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  // ─── Store Management ─────────────────────────────────────────────────────
  const handleAddStore = () => {
    setEditingStore(null);
    setStoreDialogOpen(true);
  };

  const handleEditStore = (store) => {
    setEditingStore(store);
    setStoreDialogOpen(true);
  };

  const handleStoreDialogClose = () => {
    setStoreDialogOpen(false);
    setEditingStore(null);
  };

  const handleStoreSave = async (storeForm, storeId) => {
    try {
      if (storeId) {
        // Update existing store
        await updateStore(storeId, {
          ...storeForm,
          companyId: parseInt(companyId),
        });
        toast.success('Store updated successfully');
      } else {
        // Create new store
        await createStore({
          ...storeForm,
          companyId: parseInt(companyId),
        });
        toast.success('Store created successfully');
      }
      setStoreDialogOpen(false);
      setEditingStore(null);
      await loadCompanyData();
    } catch (e) {
      throw e; // Let StoreDialog handle the error display
    }
  };

  const handleDeleteStore = (store) => {
    setDeleteStoreTarget(store);
  };

  const handleConfirmDeleteStore = async () => {
    if (!deleteStoreTarget?.Id && !deleteStoreTarget?.id) {
      toast.error('Cannot delete: store ID not found');
      setDeleteStoreTarget(null);
      return;
    }
    
    if (stores.length <= 1) {
      toast.error('Cannot delete the last store. At least one store is required.');
      setDeleteStoreTarget(null);
      return;
    }
    
    setDeletingStore(true);
    try {
      const storeId = deleteStoreTarget.Id || deleteStoreTarget.id;
      await deleteStore(storeId);
      toast.success(`Store deleted successfully`);
      setDeleteStoreTarget(null);
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setDeletingStore(false);
    }
  };

  // ─── Company Status Management ────────────────────────────────────────────
  const handleToggleCompanyStatus = async () => {
    if (!company) return;
    
    const isActive = company.isActive;
    const action = isActive ? 'deactivate' : 'activate';
    
    try {
      setSaving(true);
      if (isActive) {
        await deactivateCompany(companyId);
        toast.success('Company deactivated successfully');
      } else {
        await activateCompany(companyId);
        toast.success('Company activated successfully');
      }
      await loadCompanyData();
    } catch (e) {
      toast.error(`Failed to ${action} company: ${e?.response?.data?.message || e?.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ─── Role & Permissions Management ────────────────────────────────────────
  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setPermissionsLoading(true);
    try {
      const userId = user.UserId || user.userId || user.Id || user.id;
      const response = await getUserPermissions(userId);
      // Extract permissions from payload wrapper
      const permissions = response?.payload || response || {};
      setUserPermissions(permissions);
    } catch (e) {
      toast.error('Failed to load user permissions');
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handlePermissionChange = (moduleName, permKey, value) => {
    setUserPermissions(prev => ({
      ...prev,
      [moduleName]: {
        ...prev[moduleName],
        [permKey]: value,
      },
    }));
  };

  const handleRoleChange = async (user, newRoleId) => {
    // Check if user is company admin
    const isCompanyAdmin = user.IsCompanyAdmin || user.isCompanyAdmin || 
                          user.RoleName === 'CompanyAdmin' || user.roleName === 'CompanyAdmin' ||
                          (user.Roles && user.Roles.includes('CompanyAdmin')) ||
                          (user.roles && user.roles.includes('CompanyAdmin'));
    
    if (isCompanyAdmin) {
      toast.error('Cannot change role for Company Admin');
      return;
    }
    
    try {
      const userId = user.UserId || user.userId || user.Id || user.id;
      await changeUserRole(userId, newRoleId);
      toast.success('User role updated successfully');
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to update role');
    }
  };

  const handlePermissionsSave = async (userId, permissions) => {
    try {
      // Backend expects { CompanyId, Permissions } format
      await updateUserPermissions(userId, {
        CompanyId: parseInt(companyId),
        Permissions: permissions,
      });
      toast.success('Permissions updated successfully');
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to update permissions');
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    const userId = selectedUser.UserId || selectedUser.userId || selectedUser.Id || selectedUser.id;
    await handlePermissionsSave(userId, userPermissions);
  };

  // ─── User Management Tab Handlers ────────────────────────────────────────
  const getUserId = (user) => user?.UserId || user?.userId || user?.Id || user?.id;

  const openUserActionMenu = (e, user) => {
    e.stopPropagation();
    setUserActionMenuAnchor(e.currentTarget);
    setUserActionMenuTarget(user);
  };

  const closeUserActionMenu = () => {
    setUserActionMenuAnchor(null);
    setUserActionMenuTarget(null);
  };

  const openDialog = (type, user) => {
    setDlgField1('');
    setDlgField2('');
    setDlgField3('');
    setUserMgmtDialog({ type, user, open: true });
    closeUserActionMenu();
  };

  const closeDialog = () => {
    if (userMgmtLoading) return;
    setUserMgmtDialog({ type: null, user: null, open: false });
    setDlgField1('');
    setDlgField2('');
    setDlgField3('');
  };

  const handleToggleUserStatus = async (user) => {
    closeUserActionMenu();
    const userId = getUserId(user);
    const isActive = user.IsActive ?? user.isActive ?? true;
    setUserMgmtLoading(true);
    try {
      if (isActive) {
        await disableUser(userId);
        toast.success('User deactivated successfully');
      } else {
        await enableUser(userId);
        toast.success('User activated successfully');
      }
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setUserMgmtLoading(false);
    }
  };

  const handleUserMgmtConfirm = async () => {
    const { type, user } = userMgmtDialog;
    if (!user) return;
    const userId = getUserId(user);
    setUserMgmtLoading(true);
    try {
      if (type === 'changePassword') {
        if (!dlgField1 || dlgField1 !== dlgField2) {
          toast.error('Passwords do not match');
          setUserMgmtLoading(false);
          return;
        }
        await adminChangePassword(userId, dlgField1, dlgField2);
        toast.success('Password changed successfully');
      } else if (type === 'changeUsername') {
        if (!dlgField1.trim()) { toast.error('Username is required'); setUserMgmtLoading(false); return; }
        await adminChangeUsername(userId, dlgField1.trim());
        toast.success('Username changed successfully');
      } else if (type === 'changeEmail') {
        if (!dlgField1.trim()) { toast.error('Email is required'); setUserMgmtLoading(false); return; }
        await adminChangeEmail(userId, dlgField1.trim());
        toast.success('Email changed successfully');
      } else if (type === 'changeRole') {
        if (!dlgField1) { toast.error('Please select a role'); setUserMgmtLoading(false); return; }
        await changeUserRole(userId, dlgField1);
        toast.success('Role changed successfully');
      } else if (type === 'assignToStore') {
        if (!dlgField1) { toast.error('Please select a store'); setUserMgmtLoading(false); return; }
        await assignUserToStore(userId, parseInt(dlgField1));
        toast.success('User assigned to store successfully');
      } else if (type === 'removeFromStore') {
        await removeUserFromStore(userId);
        toast.success('User removed from store successfully');
      } else if (type === 'deleteUser') {
        await deleteUser(userId);
        toast.success('User deleted successfully');
      }
      closeDialog();
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setUserMgmtLoading(false);
    }
  };

  // ─── Handle form changes ──────────────────────────────────────────────────
  const handleCompanyFormChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm(prev => {
      const updated = { ...prev, [name]: value };
      // Check if form has changes
      const changed = JSON.stringify(updated) !== JSON.stringify(originalCompanyForm);
      setHasChanges(changed);
      return updated;
    });
  };

  // ─── Handle Cancel Changes ────────────────────────────────────────────────
  const handleCancelChanges = () => {
    setCompanyForm(originalCompanyForm);
    setHasChanges(false);
  };

  // ─── Handle Update Company ────────────────────────────────────────────────
  const handleUpdateCompany = async () => {
    try {
      setSaving(true);
      await updateCompanyDetails({
        CompanyId: parseInt(companyId),
        CompanyName: companyForm.name,
        Email: companyForm.email,
        PhoneNumber: companyForm.phoneNumber,
        BTWNumber: companyForm.btwNumber,
        IBANumber: companyForm.ibaNumber,
        BICNumber: companyForm.bicNumber,
        RPRNumber: companyForm.rprNumber,
        Website: companyForm.website,
        CompanyPeppolId: companyForm.companyPeppolId,
        PeppolUserId: companyForm.peppolUserId,
        PeppolUserMotdePasse: companyForm.peppolUserMotdePasse,
        SendInvoiceLimit: companyForm.sendInvoiceLimit ? parseInt(companyForm.sendInvoiceLimit) : null,
        SubscriptionFee: companyForm.subscriptionFee ? parseFloat(companyForm.subscriptionFee) : null,
        Street: companyForm.street,
        StreetNumber: companyForm.streetNumber,
        PostCode: companyForm.postCode,
        City: companyForm.city,
        Country: companyForm.country,
      });
      toast.success('Company information updated successfully');
      setOriginalCompanyForm(companyForm);
      setHasChanges(false);
      await loadCompanyData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update company information');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin-user-management')} sx={{ mt: 2 }}>
          Back to Companies
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f6fa', minHeight: '100vh', pb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', py: 1.5, px: 3 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ fontSize: '0.875rem' }}>
            <Link
              component="button"
              onClick={() => navigate('/admin-user-management')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Admin Dashboard
            </Link>
            <Typography color="text.primary" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {company?.name || 'Company Details'}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {/* Header */}
      <Paper sx={{ borderRadius: 0, borderBottom: '1px solid #e0e0e0', mb: 3 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate('/admin-user-management')} sx={{ bgcolor: '#f5f5f5' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar sx={{
                width: 48, height: 48, borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {company?.name}
                  </Typography>
                  <Chip
                    label={company?.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={company?.isActive ? 'success' : 'default'}
                    sx={{ fontWeight: 700, height: 24 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Edit company details, stores, and permissions
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={company?.isActive ? <DeactivateIcon /> : <ActivateIcon />}
                onClick={handleToggleCompanyStatus}
                disabled={saving}
                color={company?.isActive ? 'error' : 'success'}
                sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
              >
                {company?.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </Box>
          </Box>

          {/* Comprehensive Statistics Cards */}
          {(companyStats || statsLoading) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Company Statistics
              </Typography>
              
              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <>
                  {/* First Row: Basic Info */}
                  <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Card sx={{ 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                        border: '2px solid',
                        borderColor: '#667eea',
                        height: '100%',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.25)',
                        }
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box sx={{ 
                              bgcolor: 'rgba(102, 126, 234, 0.15)', 
                              borderRadius: '50%', 
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <PeopleIcon sx={{ fontSize: 28, color: '#667eea' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#667eea">{users?.length || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Users</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Card sx={{ 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.08) 0%, rgba(245, 87, 108, 0.08) 100%)',
                        border: '2px solid',
                        borderColor: '#f093fb',
                        height: '100%',
                        boxShadow: '0 2px 8px rgba(240, 147, 251, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(240, 147, 251, 0.25)',
                        }
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box sx={{ 
                              bgcolor: 'rgba(240, 147, 251, 0.15)', 
                              borderRadius: '50%', 
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <StoreIcon sx={{ fontSize: 28, color: '#f093fb' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#f093fb">{stores?.length || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Stores</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Card sx={{ 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.08) 0%, rgba(0, 242, 254, 0.08) 100%)',
                        border: '2px solid',
                        borderColor: '#4facfe',
                        height: '100%',
                        boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(79, 172, 254, 0.25)',
                        }
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box sx={{ 
                              bgcolor: 'rgba(79, 172, 254, 0.15)', 
                              borderRadius: '50%', 
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <CustomerIcon sx={{ fontSize: 28, color: '#4facfe' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#4facfe">{companyStats?.CustomerCount || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Customers</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Card sx={{ 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, rgba(250, 112, 154, 0.08) 0%, rgba(254, 225, 64, 0.08) 100%)',
                        border: '2px solid',
                        borderColor: '#fa709a',
                        height: '100%',
                        boxShadow: '0 2px 8px rgba(250, 112, 154, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(250, 112, 154, 0.25)',
                        }
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box sx={{ 
                              bgcolor: 'rgba(250, 112, 154, 0.15)', 
                              borderRadius: '50%', 
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ProductIcon sx={{ fontSize: 28, color: '#fa709a' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#fa709a">{companyStats?.ProductsCount || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Products</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Card sx={{ 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.08) 0%, rgba(56, 249, 215, 0.08) 100%)',
                        border: '2px solid',
                        borderColor: '#43e97b',
                        height: '100%',
                        boxShadow: '0 2px 8px rgba(67, 233, 123, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(67, 233, 123, 0.25)',
                        }
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box sx={{ 
                              bgcolor: 'rgba(67, 233, 123, 0.15)', 
                              borderRadius: '50%', 
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <SupplierIcon sx={{ fontSize: 28, color: '#43e97b' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#43e97b">{companyStats?.SupplierCount || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Suppliers</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Second Row: Module Cards */}
                  <Grid container spacing={1.5}>
                    {companyStats?.HasOnlineStore && (
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Card sx={{ 
                          borderRadius: 2, 
                          background: 'linear-gradient(135deg, rgba(247, 151, 30, 0.08) 0%, rgba(255, 210, 0, 0.08) 100%)',
                          border: '2px solid',
                          borderColor: '#f7971e',
                          height: '100%',
                          boxShadow: '0 2px 8px rgba(247, 151, 30, 0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 16px rgba(247, 151, 30, 0.25)',
                          }
                        }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Box sx={{ 
                                bgcolor: 'rgba(247, 151, 30, 0.15)', 
                                borderRadius: '50%', 
                                p: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <OnlineStoreIcon sx={{ fontSize: 28, color: '#f7971e' }} />
                              </Box>
                              <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#f7971e">✓</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600} color="text.secondary">Online Store</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                  {/* Invoices Module */}
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.08) 100%)',
                      border: '2px solid',
                      borderColor: '#4caf50',
                      height: '100%',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.25)',
                      }
                    }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Box sx={{ 
                            bgcolor: 'rgba(76, 175, 80, 0.15)', 
                            borderRadius: '50%', 
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <ReceiptIcon sx={{ fontSize: 28, color: '#4caf50' }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#4caf50">
                            {(companyStats?.PeppolInvoicesCount || 0) + (companyStats?.NonPeppolInvoicesCount || 0) + (companyStats?.DraftInvoicesCount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">Invoices</Typography>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Draft:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.DraftInvoicesCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Peppol:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.PeppolInvoicesCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Non-Peppol:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.NonPeppolInvoicesCount || 0}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Tickets Module */}
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.08) 100%)',
                      border: '2px solid',
                      borderColor: '#ff9800',
                      height: '100%',
                      boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(255, 152, 0, 0.25)',
                      }
                    }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Box sx={{ 
                            bgcolor: 'rgba(255, 152, 0, 0.15)', 
                            borderRadius: '50%', 
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <TicketIcon sx={{ fontSize: 28, color: '#ff9800' }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#ff9800">
                            {(companyStats?.SaleTicketCount || 0) + (companyStats?.RepairTicketsCount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">Tickets</Typography>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Sale:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.SaleTicketCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Repair:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.RepairTicketsCount || 0}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Sale Orders Module */}
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.08) 100%)',
                      border: '2px solid',
                      borderColor: '#2196f3',
                      height: '100%',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(33, 150, 243, 0.25)',
                      }
                    }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Box sx={{ 
                            bgcolor: 'rgba(33, 150, 243, 0.15)', 
                            borderRadius: '50%', 
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <SaleOrderIcon sx={{ fontSize: 28, color: '#2196f3' }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#2196f3">
                            {(companyStats?.DraftSaleOrderCount || 0) + (companyStats?.ClosedSaleOrderCount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">Sale Orders</Typography>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Draft:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.DraftSaleOrderCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Closed:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.ClosedSaleOrderCount || 0}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Documents Module */}
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(156, 39, 176, 0.08) 100%)',
                      border: '2px solid',
                      borderColor: '#9c27b0',
                      height: '100%',
                      boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(156, 39, 176, 0.25)',
                      }
                    }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Box sx={{ 
                            bgcolor: 'rgba(156, 39, 176, 0.15)', 
                            borderRadius: '50%', 
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <DescriptionIcon sx={{ fontSize: 28, color: '#9c27b0' }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#9c27b0">
                            {(companyStats?.OfferCount || 0) + (companyStats?.DeliveryNotesCount || 0) + (companyStats?.CreditNotesCount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">Documents</Typography>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Offers:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.OfferCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Delivery:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.DeliveryNotesCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Credit:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.CreditNotesCount || 0}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Purchase & Incoming */}
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.08) 0%, rgba(233, 30, 99, 0.08) 100%)',
                      border: '2px solid',
                      borderColor: '#e91e63',
                      height: '100%',
                      boxShadow: '0 2px 8px rgba(233, 30, 99, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(233, 30, 99, 0.25)',
                      }
                    }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Box sx={{ 
                            bgcolor: 'rgba(233, 30, 99, 0.15)', 
                            borderRadius: '50%', 
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <PurchaseIcon sx={{ fontSize: 28, color: '#e91e63' }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700} fontSize="1.25rem" color="#e91e63">
                            {(companyStats?.PurchaseInvoicesCount || 0) + (companyStats?.PeppolIncomingInvoicesCount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">Purchases</Typography>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Purchase:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.PurchaseInvoicesCount || 0}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontSize="0.7rem">Peppol In:</Typography>
                            <Typography variant="caption" fontSize="0.7rem" fontWeight={600}>{companyStats?.PeppolIncomingInvoicesCount || 0}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                </>
              )}
            </Box>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab 
              icon={<BusinessIcon />} 
              label="Company Information" 
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              icon={<GroupIcon />} 
              label="Stores & Users" 
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              icon={<ShieldIcon />} 
              label="Role & Permissions" 
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
        {/* Tab 0: Company Information */}
        <TabPanel value={activeTab} index={0}>
          <Paper sx={{ p: 3, borderRadius: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Company Details
            </Typography>
            <Grid container spacing={2.5}>
              {COMPANY_FIELDS.map(({ name, label, required, grid, type }) => (
                <Grid item xs={12} sm={grid} key={name}>
                  <TextField
                    fullWidth
                    size="small"
                    label={label}
                    name={name}
                    type={type || 'text'}
                    required={required}
                    value={companyForm[name] ?? ''}
                    onChange={handleCompanyFormChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
              ))}
            </Grid>
            
            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3} pt={3} borderTop="1px solid" borderColor="divider">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelChanges}
                disabled={!hasChanges || saving}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 2,
                  minWidth: 120
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveCompanyInfo}
                disabled={!hasChanges || saving}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  minWidth: 120
                }}
              >
                {saving ? 'Updating…' : 'Update'}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Tab 1: Stores & Users */}
        <TabPanel value={activeTab} index={1}>
          {/* Unassigned Users Section */}
          {groupedUsers.unassigned && groupedUsers.unassigned.length > 0 && (
            <Card sx={{ mb: 3, borderRadius: 2.5, border: '2px dashed', borderColor: 'warning.main' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonOffIcon color="warning" />
                  <Typography variant="h6" fontWeight={600}>
                    Unassigned Users ({groupedUsers.unassigned.length})
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These users are not assigned to any store. Use "Assign to Store" action to assign them.
                </Alert>
                <Grid container spacing={2}>
                  {groupedUsers.unassigned.map(user => (
                    <Grid item xs={12} sm={6} md={4} key={user.UserId || user.userId || user.Id || user.id}>
                      <UserCard user={user} onMenuOpen={openUserActionMenu} />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Stores Section */}
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  All Stores ({stores.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View stores and their assigned users
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddStore}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Add Store
              </Button>
            </Box>
          </Box>

          {/* Stores with Users */}
          {stores.length > 0 ? (
            <Grid container spacing={3}>
              {stores.map(store => {
                const storeId = store.Id || store.id;
                const storeUsers = groupedUsers[storeId] || [];
                return (
                  <Grid item xs={12} md={6} key={storeId}>
                    <Card sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        {/* Store Header */}
                        <Box 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="space-between"
                          mb={2}
                          pb={2}
                          borderBottom="2px solid"
                          borderColor="divider"
                        >
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                              <StoreIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {store.StoreName || store.storeName}
                              </Typography>
                              {(store.StoreCode || store.storeCode) && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  🏷️ {store.StoreCode || store.storeCode}
                                </Typography>
                              )}
                              {(store.City || store.city) && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <LocationIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {store.City || store.city}{(store.Country || store.country) && `, ${store.Country || store.country}`}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              icon={<GroupIcon sx={{ fontSize: '14px !important' }} />}
                              label={storeUsers.length}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 700, mr: 1 }}
                            />
                            <Tooltip title="Edit Store">
                              <IconButton size="small" onClick={() => handleEditStore(store)} sx={{ color: '#ff9800' }}>
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={stores.length <= 1 ? 'Cannot delete last store' : 'Delete Store'}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteStore(store)} 
                                  disabled={stores.length <= 1}
                                  sx={{ color: stores.length <= 1 ? 'action.disabled' : '#f44336' }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Store Status & Description */}
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Chip
                            label={(store.IsActive ?? store.isActive) ? 'Active' : 'Inactive'}
                            size="small"
                            color={(store.IsActive ?? store.isActive) ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                          {(store.PhoneNumber || store.phoneNumber) && (
                            <Typography variant="caption" color="text.secondary">
                              📞 {store.PhoneNumber || store.phoneNumber}
                            </Typography>
                          )}
                        </Box>

                        {/* Users in Store */}
                        <Box>
                          {storeUsers.length > 0 ? (
                            storeUsers.map(user => (
                              <UserCard key={user.UserId || user.userId || user.Id || user.id} user={user} onMenuOpen={openUserActionMenu} />
                            ))
                          ) : (
                            <Box 
                              sx={{ 
                                textAlign: 'center', 
                                py: 3,
                                color: 'text.secondary',
                                backgroundColor: 'action.hover',
                                borderRadius: 2,
                              }}
                            >
                              <PersonOffIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                              <Typography variant="body2">
                                No users assigned to this store
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Card sx={{ textAlign: 'center', py: 6, borderRadius: 2.5 }}>
              <StoreIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Stores Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Get started by creating your first store
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddStore}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Add Store
              </Button>
            </Card>
          )}
        </TabPanel>

        {/* Tab 2: Role & Permissions */}
        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: 3, borderRadius: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Users, Roles & Permissions
            </Typography>
            
            {users.length === 0 ? (
              <Alert severity="info">No users found for this company.</Alert>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a user to view and update their role and permissions. 
                  <strong> Company Admin roles cannot be changed.</strong>
                </Typography>
                
                <Grid container spacing={2}>
                  {/* User List */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 600, overflow: 'auto' }}>
                      {users.map((user, idx) => {
                        const userId = user.UserId || user.userId || user.Id || user.id;
                        const userName = user.UserName || user.userName || user.Name || user.name || user.FullName || user.fullName;
                        const userEmail = user.Email || user.email;
                        const isSelected = (selectedUser?.UserId || selectedUser?.userId || selectedUser?.Id || selectedUser?.id) === userId;
                        const isCompanyAdmin = user.IsCompanyAdmin || user.isCompanyAdmin || 
                                              user.RoleName === 'CompanyAdmin' || user.roleName === 'CompanyAdmin' ||
                                              (user.Roles && user.Roles.includes('CompanyAdmin')) ||
                                              (user.roles && user.roles.includes('CompanyAdmin'));
                        const userRole = user.RoleName || user.roleName || (user.Roles?.[0]) || (user.roles?.[0]) || 'User';
                        
                        return (
                          <Box
                            key={userId}
                            onClick={() => handleUserSelect(user)}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              bgcolor: isSelected ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                              borderBottom: idx < users.length - 1 ? '1px solid #e0e0e0' : 'none',
                              '&:hover': { bgcolor: isSelected ? 'rgba(102, 126, 234, 0.15)' : '#f5f5f5' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: '#667eea', fontSize: '0.85rem' }}>
                                {userName?.charAt(0) || 'U'}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                  {userName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {userEmail}
                                </Typography>
                                <Chip
                                  label={userRole}
                                  size="small"
                                  color={isCompanyAdmin ? 'error' : 'primary'}
                                  sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                              </Box>
                              {isSelected && <CheckIcon sx={{ color: '#667eea' }} />}
                            </Box>
                          </Box>
                        );
                      })}
                    </Paper>
                  </Grid>

                  {/* Permissions Editor */}
                  <Grid item xs={12} md={8}>
                    {!selectedUser ? (
                      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                        <ShieldIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Select a user to view and edit permissions
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            {selectedUser.UserName || selectedUser.userName || selectedUser.Name || selectedUser.name || selectedUser.FullName || selectedUser.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedUser.Email || selectedUser.email}
                          </Typography>
                        </Box>

                        {/* Role Selection */}
                        {!(selectedUser.IsCompanyAdmin || selectedUser.isCompanyAdmin || 
                           selectedUser.RoleName === 'CompanyAdmin' || selectedUser.roleName === 'CompanyAdmin' ||
                           (selectedUser.Roles && selectedUser.Roles.includes('CompanyAdmin')) ||
                           (selectedUser.roles && selectedUser.roles.includes('CompanyAdmin'))) && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Change Role
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {availableRoles.map(role => {
                                const currentRole = selectedUser.RoleName || selectedUser.roleName || 
                                                   selectedUser.Roles?.[0] || selectedUser.roles?.[0];
                                const roleName = role.Name || role.name;
                                return (
                                  <Chip
                                    key={role.Id || role.id}
                                    label={roleName}
                                    onClick={() => handleRoleChange(selectedUser, role.Id || role.id)}
                                    color={currentRole === roleName ? 'primary' : 'default'}
                                    sx={{ fontWeight: 600 }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {(selectedUser.IsCompanyAdmin || selectedUser.isCompanyAdmin ||
                          selectedUser.RoleName === 'CompanyAdmin' || selectedUser.roleName === 'CompanyAdmin' ||
                          (selectedUser.Roles && selectedUser.Roles.includes('CompanyAdmin')) ||
                          (selectedUser.roles && selectedUser.roles.includes('CompanyAdmin'))) && (
                          <Alert severity="warning" sx={{ mb: 3 }}>
                            This user is a Company Admin. Their role cannot be changed.
                          </Alert>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Permissions Matrix */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Module Permissions
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={handleSavePermissions}
                              sx={{
                                fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              }}
                            >
                              Save Permissions
                            </Button>
                          </Box>
                          <PermissionsMatrix
                            modules={allModules}
                            permissions={userPermissions}
                            onChange={handlePermissionChange}
                            loading={permissionsLoading}
                          />
                        </Box>
                      </Paper>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </TabPanel>
      </Box>

      {/* Store Dialog */}
      <StoreDialog
        open={storeDialogOpen}
        onClose={handleStoreDialogClose}
        mode={editingStore ? 'edit' : 'create'}
        store={editingStore}
        onSave={handleStoreSave}
      />

      {/* Delete Store Confirmation */}
      <ConfirmDeleteStoreDialog
        open={!!deleteStoreTarget}
        store={deleteStoreTarget}
        onConfirm={handleConfirmDeleteStore}
        onClose={() => setDeleteStoreTarget(null)}
        loading={deletingStore}
      />

      {/* User Action Menu */}
      <Menu
        anchorEl={userActionMenuAnchor}
        open={Boolean(userActionMenuAnchor)}
        onClose={closeUserActionMenu}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 210 } }}
      >
        <MenuItem onClick={() => openDialog('changePassword', userActionMenuTarget)}>
          <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openDialog('changeUsername', userActionMenuTarget)}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Change Username</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openDialog('changeEmail', userActionMenuTarget)}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Change Email</ListItemText>
        </MenuItem>
        {!(userActionMenuTarget?.IsCompanyAdmin || userActionMenuTarget?.isCompanyAdmin ||
           userActionMenuTarget?.RoleName === 'CompanyAdmin' || userActionMenuTarget?.roleName === 'CompanyAdmin' ||
           (userActionMenuTarget?.Roles && userActionMenuTarget?.Roles.includes('CompanyAdmin'))) && (
          <MenuItem onClick={() => openDialog('changeRole', userActionMenuTarget)}>
            <ListItemIcon><RoleIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Change Role</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => openDialog('assignToStore', userActionMenuTarget)}>
          <ListItemIcon><StoreAssignIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign to Store</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openDialog('removeFromStore', userActionMenuTarget)}>
          <ListItemIcon><RemoveIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText>Remove from Store</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openDialog('deleteUser', userActionMenuTarget)} sx={{ color: '#f44336' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* User Management Dialogs */}
      <Dialog
        open={userMgmtDialog.open}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        {userMgmtDialog.type === 'changePassword' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              <LockIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Change Password
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set a new password for <strong>{userMgmtDialog.user?.FullName || userMgmtDialog.user?.UserName}</strong>
              </Typography>
              <TextField
                fullWidth size="small" label="New Password" type="password"
                value={dlgField1} onChange={e => setDlgField1(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label="Confirm Password" type="password"
                value={dlgField2} onChange={e => setDlgField2(e.target.value)}
              />
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'changeUsername' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              <PersonIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Change Username
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current: <strong>{userMgmtDialog.user?.UserName}</strong>
              </Typography>
              <TextField
                fullWidth size="small" label="New Username"
                value={dlgField1} onChange={e => setDlgField1(e.target.value)}
              />
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'changeEmail' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              <EmailIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Change Email
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current: <strong>{userMgmtDialog.user?.Email}</strong>
              </Typography>
              <TextField
                fullWidth size="small" label="New Email" type="email"
                value={dlgField1} onChange={e => setDlgField1(e.target.value)}
              />
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'changeRole' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              <RoleIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Change Role
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a new role for <strong>{userMgmtDialog.user?.FullName || userMgmtDialog.user?.UserName}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select value={dlgField1} onChange={e => setDlgField1(e.target.value)} label="Role">
                  {availableRoles
                    .filter(r => (r.Name || r.name) !== 'SuperAdmin')
                    .map(r => (
                      <MenuItem key={r.Id || r.id} value={r.Name || r.name}>
                        {r.Name || r.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'assignToStore' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              <StoreAssignIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Assign to Store
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign <strong>{userMgmtDialog.user?.FullName || userMgmtDialog.user?.UserName}</strong> to a store
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Store</InputLabel>
                <Select value={dlgField1} onChange={e => setDlgField1(e.target.value)} label="Store">
                  {stores.map(s => (
                    <MenuItem key={s.Id || s.id} value={String(s.Id || s.id)}>
                      {s.StoreName || s.storeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'removeFromStore' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#ff9800' }}>
              <RemoveIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Remove from Store
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Remove <strong>{userMgmtDialog.user?.FullName || userMgmtDialog.user?.UserName}</strong> from their current store?
              </Typography>
            </DialogContent>
          </>
        )}
        {userMgmtDialog.type === 'deleteUser' && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#f44336' }}>
              <WarningIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Delete User
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Permanently delete <strong>{userMgmtDialog.user?.FullName || userMgmtDialog.user?.UserName}</strong>?
                This action cannot be undone.
              </Typography>
            </DialogContent>
          </>
        )}
        {userMgmtDialog.open && (
          <>
            <Divider />
            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
              <Button onClick={closeDialog} disabled={userMgmtLoading} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
                Cancel
              </Button>
              <Button
                onClick={handleUserMgmtConfirm}
                variant="contained"
                disabled={userMgmtLoading}
                startIcon={userMgmtLoading ? <CircularProgress size={14} color="inherit" /> : null}
                color={userMgmtDialog.type === 'deleteUser' ? 'error' : 'primary'}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
                  ...(userMgmtDialog.type !== 'deleteUser' && {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }),
                }}
              >
                {userMgmtLoading ? 'Processing…' : userMgmtDialog.type === 'deleteUser' ? 'Delete' :
                  userMgmtDialog.type === 'removeFromStore' ? 'Remove' : 'Confirm'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CompanyEditPage;
